import { useDialog } from '@/common/dialog/DialogContext';
import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { useNavState } from '@/common/navState/NavStateContext';
import { usePrincipal } from '@/common/principal/PrincipalContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { ObjUtils } from '@/common/utils/ObjUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { SamplePpCalcDialog } from './SamplePpCalcDialog';

/**
 * Per requirement GP-15,this provides a view of the Price Points within a Best Price Calculation
 */
export const CalcProdPpList = ({ calc, calcProd }) => {

  const { hasRole } = usePrincipal();
  const { navigateTo } = useNavState();
  const  rsrc = useRsrc();
  const { openDialog } = useDialog();

  // Toggle: show only this NDC11 or also linked products
  const [hasLinked, setHasLinked] = useState(false);
  const [showLinked, setShowLinked] = useState(false);
  const [calcProdIds, setCalcProdIds] = useState([]);

  const [calcProdIdToCodeMap, setCalcProdIdToCodeMap] = useState({});

  // Fetch linked NDC11s and determine if toggle should show
  useEffect(() => {
    if (!calcProd) return;
    rsrc.rows('gp', 'CALC_PROD_RES', { expr: ExprUtils.whereParams('calcProdId', calcProd.id, 'code', 'LINKED11') }).then((linked11Rows) => {
      const linked11 = linked11Rows[0]?.value?.split(',');
      if (linked11.length > 1) {
        setHasLinked(true);
      } else {
        setHasLinked(false);
      }
      
      if (showLinked && linked11.length > 0) {
        rsrc.rows('gp', 'CALC_PROD', { expr: ExprUtils.whereParams('calcId', calc.id, 'code', linked11)}).then((linkedProds) => {
          const linkedProdIds = linkedProds.map(lp => lp.id);
          const newMap = {};
          linkedProds.forEach(lp => newMap[lp.id] = lp.code);
          setCalcProdIdToCodeMap(newMap);
          setCalcProdIds(linkedProdIds);
        });
      } else {
        setCalcProdIds([calcProd.id]);
        setCalcProdIdToCodeMap({});
      }
    });
  }, [showLinked, calcProd]);

  const augRowsReq = useCallback((rowsReq) => {
    if (!calcProd) return;
    
    rowsReq.expr = ExprUtils.and(rowsReq.expr, ExprUtils.whereParams('calcProdId', calcProdIds));
    
  }, [calcProd, calcProdIds]);


  const augFieldDefs = useCallback((fieldToFieldDef) => {
    const columns = ['price','trxLink', 'ppSrcCode', 'trxCount'];

    Object.keys(fieldToFieldDef).forEach(colName => {
      if (!columns.includes(colName)) {
        delete fieldToFieldDef[colName];
      } else {
        const index = columns.indexOf(colName);
        fieldToFieldDef[colName].precedence = (index + 1) * 100
      }
    });

    fieldToFieldDef.price = {
      ...fieldToFieldDef.price,
      sort: 'asc',
      type: 'CALC_VALUE',
    };

    fieldToFieldDef.trxLink.displayName = 'Sample Transaction Link';
    fieldToFieldDef.ppSrcCode.displayName = 'Price Point Source';
    fieldToFieldDef.trxCount.displayName = 'Transaction Set Count';

    if (showLinked) {
      
      fieldToFieldDef.linkedCalcProduct = {
        field: 'linkedCalcProduct',
        displayName: 'Linked NDC11',
        maxWidth: 250,
        // TODO: Not linking correctly
        // module: 'gp',
        // entity: 'CALC_PROD',
        // type: 'HYPERLINK',
        valueGetter: (params) => {
          if (params.data?.calcProdId === calcProd.id) {
            return null;
          } else {
           return calcProdIdToCodeMap[params.data?.calcProdId];
          }
        }
      };

      fieldToFieldDef.linkedProdPp = {
        field: 'linkedProdPp',
        displayName: 'Linked Product Price Point',
        type: 'BOOLEAN',
        valueGetter: (params) => params.data?.calcProdId !== calcProd.id,
        maxWidth: 250
      };

    }
  }, [showLinked, calcProd.id, calcProdIdToCodeMap]);

  const openSampleCalcDialog = (rowData) => {
    const data = {
      sampleJson: rowData.sampleJson,
      module: 'gp'
    }
    openDialog(SamplePpCalcDialog, { data })
  }

  const getContextMenuItems = (params) => {
    const data = params.node.data;

    const contextMenuItems = [];

    const hasCalcProdPpView = hasRole('GP_CALC_PROD_PP_VIEW');

    if (hasCalcProdPpView) {
      const viewerReqParam = {
        code: 'CALC_PP_TRX',
        data: data,
        calc: calc,
        ndc11: calcProdIdToCodeMap[data?.calcProdId] ?? calcProd.code
      }
      contextMenuItems.push({
        name: 'Transactions',
        icon: fas.faSignInAlt,
        action: () => navigateTo('gp/pp-viewer', { viewerReqParam: viewerReqParam, entity: 'CALC_PROD_PP' })
      });
    }

    contextMenuItems.push({
      name: 'Sample Calculation',
      icon: fas.faCalculator,
      action: () => openSampleCalcDialog(data)
    });

    return contextMenuItems;
  };

 const augTabButtons = (buttons) => {
    ObjUtils.clearObj(buttons)

    if (hasLinked) {
        buttons[0] = (
        <ToggleButtonGroup
          value={showLinked ? 'linked' : 'ndc11'}
          exclusive
          onChange={(e, value) => {
            if (value !== null) setShowLinked(value === 'linked');
          }}
        >
          <ToggleButton value="ndc11">NDC11</ToggleButton>
          <ToggleButton value="linked">Linked</ToggleButton>
        </ToggleButtonGroup>
  )
    }
  };

  return (
    <EntityList
      entity="CALC_PROD_PP"
      key={`${calcProdIds.join(',')}-${hasLinked}`} // Force refresh when toggling between linked and ndc11 as they have different rows
      augRowsReq={augRowsReq}
      augTabButtons={augTabButtons}
      augFieldDefs={augFieldDefs}
      gridOptions={{getContextMenuItems}}
    />
  )
}